type FetchFn = typeof globalThis.fetch;

let nativeFetch: FetchFn | undefined;

/**
 * Caches the native fetch function before Datadog patches it.
 *
 * Must be called before Datadog initialization. Datadog's fetch patching locks response streams
 * when monitoring, causing AWS SDK S3 uploads to fail with "ReadableStream is locked" errors.
 */
export function cacheNativeFetch(): void {
    if (!nativeFetch && typeof globalThis.fetch === 'function') {
        nativeFetch = globalThis.fetch.bind(globalThis);
    }
}

export function getNativeFetch(): FetchFn | undefined {
    return nativeFetch;
}
function appendQueryParam(
    params: URLSearchParams,
    key: string,
    value:
        | string
        | number
        | boolean
        | null
        | undefined
        | Array<string | number | boolean | null | undefined>
): void {
    if (Array.isArray(value)) {
        for (const item of value) {
            appendQueryParam(params, key, item);
        }
        return;
    }
    if (value === undefined || value === null) {
        return;
    }
    params.append(key, String(value));
}

type SmithyHttpRequest = {
    protocol: string;
    hostname: string;
    port?: number;
    path: string;
    query?: Record<
        string,
        | string
        | number
        | boolean
        | null
        | undefined
        | Array<string | number | boolean | null | undefined>
    >;
    fragment?: string;
    username?: string;
    password?: string;
    headers: Record<string, string | undefined>;
    method: string;
    body?: unknown;
};
type SmithyHttpRequestOptions = {
    abortSignal?: AbortSignal | { aborted?: boolean };
    requestTimeout?: number;
};

const buildRequestFromSmithRequest = (
    request: SmithyHttpRequest,
    { abortSignal }: SmithyHttpRequestOptions
) => {
    const origin = `${request.protocol}//${request.hostname}${request.port ? `:${request.port}` : ''}`;
    const url = new URL(request.path, origin);

    for (const [key, value] of Object.entries(request.query ?? {})) {
        appendQueryParam(
            url.searchParams,
            key,
            value as
                | string
                | number
                | boolean
                | null
                | undefined
                | Array<string | number | boolean | null | undefined>
        );
    }

    if (request.fragment) {
        url.hash = request.fragment;
    }

    const headers = new Headers();
    for (const [key, value] of Object.entries(request.headers)) {
        if (value !== undefined) {
            headers.set(key, value);
        }
    }

    const init: RequestInit = {
        method: request.method,
        headers,
        body:
            request.method === 'GET' || request.method === 'HEAD'
                ? undefined
                : (request.body as BodyInit),
        signal: abortSignal as AbortSignal | undefined,
    };

    return { url, init };
};

const buildSmithyResponseFromResponse = async (response: Response) => {
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
    });

    return {
        response: {
            // Required by Smithy response shape: numeric HTTP status code.
            statusCode: response.status,
            // Required by Smithy response shape: human-readable status text.
            reason: response.statusText,
            // Required by Smithy response shape: normalized response headers.
            headers: responseHeaders,
            // Required by Smithy response shape: stream/blob payload consumed by AWS middleware.
            body: response.body ?? (await response.blob()),
        },
    };
};

/**
 * Creates a custom AWS SDK request handler that uses the cached native fetch to bypass global version patched by Datadog.
 *
 * Returns undefined if native fetch was not cached, causing AWS SDK to fall back to global fetch.
 * Use this as the `requestHandler` option when creating S3Client to avoid Datadog stream locking issues.
 *
 * TODO: stream locking issue happens when datadog and faro are initialized together. Remove s3-fetch-handler.ts when faro is removed.
 */
export function createS3RequestHandler() {
    const nativeFetch = getNativeFetch();
    if (nativeFetch) {
        return {
            async handle(
                request: SmithyHttpRequest,
                { abortSignal, requestTimeout }: SmithyHttpRequestOptions = {}
            ) {
                // if the request was already aborted, prevent doing extra work
                if (abortSignal?.aborted) {
                    const abortError = new Error('Request aborted');
                    abortError.name = 'AbortError';
                    return Promise.reject(abortError);
                }

                const { url, init } = buildRequestFromSmithRequest(request, { abortSignal });
                const fetchPromise = nativeFetch(url.toString(), init);
                let timeoutId: ReturnType<typeof setTimeout> | undefined;
                const response = await (requestTimeout && requestTimeout > 0
                    ? Promise.race([
                          fetchPromise,
                          new Promise<Response>((_, reject) => {
                              timeoutId = setTimeout(() => {
                                  reject(new Error(`Request timed out after ${requestTimeout}ms`));
                              }, requestTimeout);
                          }),
                      ])
                    : fetchPromise);

                if (timeoutId) {
                    clearTimeout(timeoutId);
                }

                return buildSmithyResponseFromResponse(response);
            },
        };
    }
}
